// PIP Intelligence - AI-Powered Performance Improvement Plan
// Hospice CareLens

import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Target,
  FileText,
  Lightbulb,
  Users,
  TrendingUp,
  Settings,
  ChevronRight,
  ChevronDown,
  Loader2,
  Plus,
  Search,
  Filter,
  BarChart3,
  Activity,
  Shield,
  DollarSign,
  Stethoscope,
  ClipboardList,
  ArrowRight,
  Sparkles,
  BookOpen,
  RefreshCw,
  Download,
  Zap,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const CATEGORY_CONFIG = {
  clinical_quality: {
    icon: Stethoscope,
    color: 'from-red-500 to-rose-600',
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-400'
  },
  operational: {
    icon: Settings,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400'
  },
  staffing: {
    icon: Users,
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-400'
  },
  regulatory: {
    icon: Shield,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400'
  },
  financial: {
    icon: DollarSign,
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-400'
  }
};

const SEVERITY_CONFIG = {
  low: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Low' },
  medium: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Medium' },
  high: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'High' },
  critical: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Critical' }
};

const STATUS_CONFIG = {
  open: { color: 'bg-blue-500/20 text-blue-400', label: 'Open' },
  in_progress: { color: 'bg-amber-500/20 text-amber-400', label: 'In Progress' },
  closed: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Closed' }
};

export default function PIPIntelligence() {
  const [activeTab, setActiveTab] = useState('create');
  const [categories, setCategories] = useState({});
  const [pips, setPips] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedPip, setSelectedPip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [trendAnalysis, setTrendAnalysis] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  // Form state
  const [problemStatement, setProblemStatement] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('medium');
  
  // Analysis result
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    rca: true,
    actions: true,
    training: false,
    process: false,
    resources: false,
    metrics: false
  });

  const token = localStorage.getItem('qapi-token');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    fetchCategories();
    fetchPips();
    fetchAnalytics();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pip/categories`, { headers });
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchPips = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pip/list`, { headers });
      const data = await res.json();
      if (data.success) {
        setPips(data.pips);
      }
    } catch (error) {
      console.error('Failed to fetch PIPs:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pip/analytics`, { headers });
      const data = await res.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const fetchTrendAnalysis = async () => {
    setLoadingTrends(true);
    try {
      const res = await fetch(`${API_URL}/api/pip/trends/analysis`, { headers });
      const data = await res.json();
      if (data.success) {
        setTrendAnalysis(data.trends);
      }
    } catch (error) {
      console.error('Failed to fetch trends:', error);
      toast.error('Failed to load trend analysis');
    } finally {
      setLoadingTrends(false);
    }
  };

  const downloadPdf = async (pipId) => {
    setDownloadingPdf(pipId);
    try {
      const res = await fetch(`${API_URL}/api/pip/${pipId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PIP_${pipId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF downloaded successfully');
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const analyzeProblem = async () => {
    if (!problemStatement.trim()) {
      toast.error('Please enter a problem statement');
      return;
    }
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    setAnalyzing(true);
    setAnalysisResult(null);

    try {
      const res = await fetch(`${API_URL}/api/pip/analyze`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          problem_statement: problemStatement,
          category: selectedCategory,
          severity: selectedSeverity
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('API error response:', res.status, errorText);
        toast.error(`Analysis failed: Server error (${res.status})`);
        return;
      }

      const data = await res.json();
      
      if (data.success) {
        setAnalysisResult(data.pip);
        toast.success('Analysis complete! PIP generated successfully.');
        fetchPips();
        fetchAnalytics();
      } else {
        toast.error(data.detail || 'Analysis failed - please try again');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        toast.error('Network error - please check your connection and try again');
      } else {
        toast.error('Failed to analyze problem - please try again');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderFiveWhys = (analysis) => {
    if (!analysis?.root_cause_analysis?.five_whys) return null;
    
    return (
      <div className="space-y-3">
        {analysis.root_cause_analysis.five_whys.map((item, idx) => (
          <div key={idx} className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 font-bold text-sm">
              {item.level}
            </div>
            <div className="flex-1 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <p className="text-cyan-400 font-medium text-sm">{item.why}</p>
              <p className="text-slate-300 mt-1">{item.answer}</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderActionPlan = (analysis) => {
    if (!analysis?.action_plan) return null;
    
    const { immediate_actions, short_term_actions, long_term_actions } = analysis.action_plan;
    
    return (
      <div className="space-y-4">
        {immediate_actions?.length > 0 && (
          <div>
            <h4 className="text-red-400 font-bold text-sm mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Immediate Actions (24-48 hours)
            </h4>
            <div className="space-y-2">
              {immediate_actions.map((action, idx) => (
                <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-white font-medium">{action.action}</p>
                  <div className="flex gap-4 mt-2 text-sm text-slate-400">
                    <span><Users className="h-3 w-3 inline mr-1" />{action.responsible}</span>
                    <span><Clock className="h-3 w-3 inline mr-1" />{action.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {short_term_actions?.length > 0 && (
          <div>
            <h4 className="text-amber-400 font-bold text-sm mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Short-Term Actions (1-2 weeks)
            </h4>
            <div className="space-y-2">
              {short_term_actions.map((action, idx) => (
                <div key={idx} className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-white font-medium">{action.action}</p>
                  <div className="flex gap-4 mt-2 text-sm text-slate-400">
                    <span><Users className="h-3 w-3 inline mr-1" />{action.responsible}</span>
                    <span><Clock className="h-3 w-3 inline mr-1" />{action.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {long_term_actions?.length > 0 && (
          <div>
            <h4 className="text-emerald-400 font-bold text-sm mb-2 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Long-Term Actions (30-90 days)
            </h4>
            <div className="space-y-2">
              {long_term_actions.map((action, idx) => (
                <div key={idx} className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <p className="text-white font-medium">{action.action}</p>
                  <div className="flex gap-4 mt-2 text-sm text-slate-400">
                    <span><Users className="h-3 w-3 inline mr-1" />{action.responsible}</span>
                    <span><Clock className="h-3 w-3 inline mr-1" />{action.timeline}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600">
            <Brain className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-white">PIP Intelligence</h1>
            <p className="text-slate-400">AI-Powered Performance Improvement Plans</p>
          </div>
        </div>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Enter a problem statement and our AI will generate a comprehensive Root Cause Analysis 
          with QAPI-compliant recommendations tailored to hospice operations.
        </p>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total PIPs</p>
                  <p className="text-2xl font-black text-white">{analytics.total_pips}</p>
                </div>
                <FileText className="h-8 w-8 text-slate-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Open</p>
                  <p className="text-2xl font-black text-blue-400">{analytics.by_status?.open || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">In Progress</p>
                  <p className="text-2xl font-black text-amber-400">{analytics.by_status?.in_progress || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Critical Open</p>
                  <p className="text-2xl font-black text-red-400">{analytics.open_critical || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === 'trends' && !trendAnalysis) {
          fetchTrendAnalysis();
        }
      }}>
        <TabsList className="bg-slate-800/50 border border-slate-700/50 mb-6">
          <TabsTrigger value="create" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-purple-600">
            <Plus className="h-4 w-4 mr-2" />
            Create New PIP
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600">
            <ClipboardList className="h-4 w-4 mr-2" />
            PIP History
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600">
            <TrendingUp className="h-4 w-4 mr-2" />
            AI Trend Analysis
          </TabsTrigger>
        </TabsList>

        {/* Create New PIP Tab */}
        <TabsContent value="create">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <Card className="bg-slate-900/80 border-slate-700/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Lightbulb className="h-5 w-5 text-amber-400" />
                  Describe the Problem
                </CardTitle>
                <CardDescription>
                  Provide details about the issue you're experiencing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Category</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(categories).map(([key, cat]) => {
                      const config = CATEGORY_CONFIG[key] || CATEGORY_CONFIG.operational;
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setSelectedCategory(key)}
                          className={`p-3 rounded-lg border transition-all ${
                            selectedCategory === key
                              ? `bg-gradient-to-br ${config.color} border-transparent`
                              : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                          }`}
                        >
                          <Icon className={`h-5 w-5 mx-auto mb-1 ${selectedCategory === key ? 'text-white' : config.textColor}`} />
                          <p className={`text-xs font-medium ${selectedCategory === key ? 'text-white' : 'text-slate-300'}`}>
                            {cat.name}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Severity Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Severity</label>
                  <div className="flex gap-2">
                    {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedSeverity(key)}
                        className={`px-4 py-2 rounded-lg border font-medium text-sm transition-all ${
                          selectedSeverity === key
                            ? config.color + ' border-transparent'
                            : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Problem Statement */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Problem Statement</label>
                  <Textarea
                    placeholder="Describe the issue in detail. Include what's happening, when it started, who is affected, and any relevant data or observations..."
                    value={problemStatement}
                    onChange={(e) => setProblemStatement(e.target.value)}
                    className="min-h-[200px] bg-slate-800/50 border-slate-700/50 text-white placeholder:text-slate-500"
                    data-testid="problem-statement-input"
                  />
                </div>

                {/* Category Examples */}
                {selectedCategory && categories[selectedCategory] && (
                  <Alert className="bg-slate-800/50 border-slate-700/50">
                    <Lightbulb className="h-4 w-4 text-amber-400" />
                    <AlertTitle className="text-amber-400">Example Issues</AlertTitle>
                    <AlertDescription className="text-slate-400">
                      {categories[selectedCategory].examples.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Analyze Button */}
                <Button
                  onClick={analyzeProblem}
                  disabled={analyzing || !problemStatement.trim() || !selectedCategory}
                  className="w-full bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 font-bold"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Analyzing with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate PIP Analysis
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results Section */}
            <div className="space-y-4">
              {analyzing && (
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardContent className="p-8">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="relative">
                        <Brain className="h-16 w-16 text-fuchsia-500 animate-pulse" />
                        <div className="absolute inset-0 h-16 w-16 rounded-full bg-fuchsia-500/20 animate-ping" />
                      </div>
                      <h3 className="text-xl font-bold text-white mt-4">AI Analysis in Progress</h3>
                      <p className="text-slate-400 mt-2">
                        Analyzing problem, gathering operational context, generating RCA...
                      </p>
                      <div className="w-full max-w-xs mt-4">
                        <Progress value={66} className="h-2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {analysisResult && !analyzing && (
                <>
                  {/* PIP Header */}
                  <Card className="bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 border-fuchsia-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge className="bg-fuchsia-500/20 text-fuchsia-400 mb-2">{analysisResult.pip_id}</Badge>
                          <h3 className="text-lg font-bold text-white">{analysisResult.problem_statement.substring(0, 100)}...</h3>
                          <div className="flex gap-2 mt-2">
                            <Badge className={SEVERITY_CONFIG[analysisResult.severity]?.color}>
                              {analysisResult.severity}
                            </Badge>
                            <Badge className={CATEGORY_CONFIG[analysisResult.category]?.bgColor + ' ' + CATEGORY_CONFIG[analysisResult.category]?.textColor}>
                              {categories[analysisResult.category]?.name}
                            </Badge>
                          </div>
                        </div>
                        <CheckCircle2 className="h-12 w-12 text-emerald-400" />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Root Cause Analysis */}
                  <Card className="bg-slate-900/80 border-slate-700/50">
                    <CardHeader 
                      className="cursor-pointer" 
                      onClick={() => toggleSection('rca')}
                    >
                      <CardTitle className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-cyan-400" />
                          Root Cause Analysis (5-Whys)
                        </div>
                        {expandedSections.rca ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </CardTitle>
                    </CardHeader>
                    {expandedSections.rca && (
                      <CardContent>
                        {/* Immediate Cause */}
                        <Alert className="bg-cyan-500/10 border-cyan-500/30 mb-4">
                          <AlertTriangle className="h-4 w-4 text-cyan-400" />
                          <AlertTitle className="text-cyan-400">Immediate Cause</AlertTitle>
                          <AlertDescription className="text-slate-300">
                            {analysisResult.analysis?.root_cause_analysis?.immediate_cause}
                          </AlertDescription>
                        </Alert>

                        {/* 5 Whys */}
                        {renderFiveWhys(analysisResult.analysis)}

                        {/* Root Cause */}
                        <Alert className="bg-fuchsia-500/10 border-fuchsia-500/30 mt-4">
                          <Brain className="h-4 w-4 text-fuchsia-400" />
                          <AlertTitle className="text-fuchsia-400">Root Cause Identified</AlertTitle>
                          <AlertDescription className="text-white font-medium">
                            {analysisResult.analysis?.root_cause_analysis?.root_cause}
                          </AlertDescription>
                        </Alert>

                        {/* Contributing Factors */}
                        {analysisResult.analysis?.root_cause_analysis?.contributing_factors && (
                          <div className="mt-4">
                            <h4 className="text-sm font-bold text-slate-300 mb-2">Contributing Factors</h4>
                            <div className="flex flex-wrap gap-2">
                              {analysisResult.analysis.root_cause_analysis.contributing_factors.map((factor, idx) => (
                                <Badge key={idx} variant="outline" className="bg-slate-800/50 text-slate-300">
                                  {factor}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    )}
                  </Card>

                  {/* Risk Assessment */}
                  {analysisResult.analysis?.risk_assessment && (
                    <Card className="bg-slate-900/80 border-slate-700/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white text-base">
                          <Shield className="h-5 w-5 text-purple-400" />
                          Risk Assessment
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          {Object.entries(analysisResult.analysis.risk_assessment).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                              <span className="text-slate-400 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                              <Badge className={
                                value === 'high' ? 'bg-red-500/20 text-red-400' :
                                value === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-emerald-500/20 text-emerald-400'
                              }>
                                {value}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Action Plan */}
                  <Card className="bg-slate-900/80 border-slate-700/50">
                    <CardHeader 
                      className="cursor-pointer" 
                      onClick={() => toggleSection('actions')}
                    >
                      <CardTitle className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-2">
                          <ClipboardList className="h-5 w-5 text-emerald-400" />
                          Action Plan
                        </div>
                        {expandedSections.actions ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </CardTitle>
                    </CardHeader>
                    {expandedSections.actions && (
                      <CardContent>
                        {renderActionPlan(analysisResult.analysis)}
                      </CardContent>
                    )}
                  </Card>

                  {/* Training Recommendations */}
                  {analysisResult.analysis?.training_recommendations?.length > 0 && (
                    <Card className="bg-slate-900/80 border-slate-700/50">
                      <CardHeader 
                        className="cursor-pointer" 
                        onClick={() => toggleSection('training')}
                      >
                        <CardTitle className="flex items-center justify-between text-white">
                          <div className="flex items-center gap-2">
                            <BookOpen className="h-5 w-5 text-blue-400" />
                            Training Recommendations
                          </div>
                          {expandedSections.training ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        </CardTitle>
                      </CardHeader>
                      {expandedSections.training && (
                        <CardContent>
                          <div className="space-y-3">
                            {analysisResult.analysis.training_recommendations.map((training, idx) => (
                              <div key={idx} className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                                <p className="text-white font-medium">{training.topic}</p>
                                <div className="flex gap-4 mt-2 text-sm text-slate-400">
                                  <span><Users className="h-3 w-3 inline mr-1" />{training.target_audience}</span>
                                  <span><FileText className="h-3 w-3 inline mr-1" />{training.format}</span>
                                  <span><Clock className="h-3 w-3 inline mr-1" />{training.timeline}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Success Metrics */}
                  {analysisResult.analysis?.success_metrics?.length > 0 && (
                    <Card className="bg-slate-900/80 border-slate-700/50">
                      <CardHeader 
                        className="cursor-pointer" 
                        onClick={() => toggleSection('metrics')}
                      >
                        <CardTitle className="flex items-center justify-between text-white">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-amber-400" />
                            Success Metrics
                          </div>
                          {expandedSections.metrics ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        </CardTitle>
                      </CardHeader>
                      {expandedSections.metrics && (
                        <CardContent>
                          <div className="space-y-3">
                            {analysisResult.analysis.success_metrics.map((metric, idx) => (
                              <div key={idx} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                                <p className="text-white font-medium">{metric.metric}</p>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                  <div className="text-center">
                                    <p className="text-xs text-slate-500">Baseline</p>
                                    <p className="text-sm text-slate-300">{metric.baseline}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-slate-500">Target</p>
                                    <p className="text-sm text-emerald-400 font-bold">{metric.target}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-xs text-slate-500">Frequency</p>
                                    <p className="text-sm text-slate-300">{metric.measurement_frequency}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Summary */}
                  {analysisResult.analysis?.summary && (
                    <Alert className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                      <AlertTitle className="text-emerald-400">Analysis Summary</AlertTitle>
                      <AlertDescription className="text-slate-300">
                        {analysisResult.analysis.summary}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}

              {!analyzing && !analysisResult && (
                <Card className="bg-slate-900/80 border-slate-700/50 border-dashed">
                  <CardContent className="p-12">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Brain className="h-16 w-16 text-slate-700 mb-4" />
                      <h3 className="text-xl font-bold text-slate-500">AI Analysis Results</h3>
                      <p className="text-slate-600 mt-2 max-w-sm">
                        Fill out the form and click "Generate PIP Analysis" to get AI-powered recommendations
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* PIP History Tab */}
        <TabsContent value="list">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-cyan-400" />
                  PIP History
                </div>
                <Button variant="outline" size="sm" onClick={fetchPips}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pips.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-500">No PIPs created yet. Create your first PIP above.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pips.map((pip) => {
                    const catConfig = CATEGORY_CONFIG[pip.category] || CATEGORY_CONFIG.operational;
                    const Icon = catConfig.icon;
                    return (
                      <div 
                        key={pip.pip_id}
                        className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:border-slate-600 transition-all cursor-pointer"
                        onClick={() => setSelectedPip(pip)}
                        data-testid={`pip-item-${pip.pip_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${catConfig.bgColor}`}>
                              <Icon className={`h-5 w-5 ${catConfig.textColor}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">{pip.pip_id}</Badge>
                                <Badge className={STATUS_CONFIG[pip.status]?.color}>
                                  {STATUS_CONFIG[pip.status]?.label}
                                </Badge>
                                <Badge className={SEVERITY_CONFIG[pip.severity]?.color}>
                                  {pip.severity}
                                </Badge>
                              </div>
                              <p className="text-white font-medium line-clamp-2">
                                {pip.problem_statement}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <p className="text-slate-500 text-sm">
                                  Created: {new Date(pip.created_at).toLocaleDateString()}
                                </p>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadPdf(pip.pip_id);
                                  }}
                                  disabled={downloadingPdf === pip.pip_id}
                                  className="h-7 px-2 text-slate-400 hover:text-white"
                                  data-testid={`download-pdf-${pip.pip_id}`}
                                >
                                  {downloadingPdf === pip.pip_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Download className="h-4 w-4 mr-1" />
                                      PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-600" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Trend Analysis Tab */}
        <TabsContent value="trends">
          <div className="space-y-6">
            {/* Trend Analysis Header */}
            <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-600/20 border-emerald-500/30">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-white">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-emerald-400" />
                    AI-Powered Trend Analysis
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchTrendAnalysis}
                    disabled={loadingTrends}
                  >
                    {loadingTrends ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription>
                  Identifies recurring patterns across PIPs and suggests preventive measures
                </CardDescription>
              </CardHeader>
            </Card>

            {loadingTrends ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Skeleton className="h-64" />
                <Skeleton className="h-64" />
              </div>
            ) : trendAnalysis ? (
              <>
                {!trendAnalysis.has_sufficient_data ? (
                  <Alert className="bg-amber-500/10 border-amber-500/30">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <AlertTitle className="text-amber-400">Insufficient Data</AlertTitle>
                    <AlertDescription className="text-slate-300">
                      {trendAnalysis.message}. Create more PIPs to enable trend analysis.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Category Breakdown */}
                    <Card className="bg-slate-900/80 border-slate-700/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white text-base">
                          <BarChart3 className="h-5 w-5 text-blue-400" />
                          Category Breakdown
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {Object.entries(trendAnalysis.category_breakdown || {}).map(([cat, count]) => {
                            const config = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.operational;
                            const Icon = config.icon;
                            const total = trendAnalysis.total_pips_analyzed || 1;
                            const percentage = Math.round((count / total) * 100);
                            return (
                              <div key={cat}>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${config.textColor}`} />
                                    <span className="text-slate-300 text-sm">{categories[cat]?.name || cat}</span>
                                  </div>
                                  <span className="text-white font-bold">{count}</span>
                                </div>
                                <Progress value={percentage} className="h-2" />
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Insights */}
                    <Card className="bg-slate-900/80 border-slate-700/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white text-base">
                          <Eye className="h-5 w-5 text-purple-400" />
                          AI Insights
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {trendAnalysis.insights?.length > 0 ? (
                            trendAnalysis.insights.map((insight, idx) => (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border ${
                                  insight.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                                  insight.severity === 'high' ? 'bg-orange-500/10 border-orange-500/30' :
                                  'bg-blue-500/10 border-blue-500/30'
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={
                                    insight.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                                    insight.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-blue-500/20 text-blue-400'
                                  }>
                                    {insight.type.replace(/_/g, ' ')}
                                  </Badge>
                                </div>
                                <p className="text-slate-300 text-sm">{insight.finding}</p>
                              </div>
                            ))
                          ) : (
                            <p className="text-slate-500">No significant patterns detected yet.</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Preventive Measures */}
                    <Card className="bg-slate-900/80 border-slate-700/50 md:col-span-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white text-base">
                          <Shield className="h-5 w-5 text-emerald-400" />
                          Recommended Preventive Measures
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {trendAnalysis.preventive_measures?.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {trendAnalysis.preventive_measures.map((measure, idx) => (
                              <div 
                                key={idx} 
                                className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <Badge className="bg-emerald-500/20 text-emerald-400">
                                    {categories[measure.category]?.name || measure.category}
                                  </Badge>
                                  <Badge className={
                                    measure.priority === 'critical' ? 'bg-red-500/20 text-red-400' :
                                    measure.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                    'bg-blue-500/20 text-blue-400'
                                  }>
                                    {measure.priority} priority
                                  </Badge>
                                </div>
                                <p className="text-white font-medium">{measure.measure}</p>
                                <p className="text-slate-400 text-sm mt-1">
                                  <Target className="h-3 w-3 inline mr-1" />
                                  Expected Impact: {measure.expected_impact}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500">Create more PIPs to generate preventive recommendations.</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Top Keywords */}
                    {trendAnalysis.top_keywords?.length > 0 && (
                      <Card className="bg-slate-900/80 border-slate-700/50 md:col-span-2">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-white text-base">
                            <Sparkles className="h-5 w-5 text-amber-400" />
                            Common Themes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex flex-wrap gap-2">
                            {trendAnalysis.top_keywords.map((kw, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className="bg-slate-800/50 text-slate-300 text-sm py-1 px-3"
                              >
                                {kw.word} <span className="text-slate-500 ml-1">({kw.count})</span>
                              </Badge>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </>
            ) : (
              <Alert className="bg-slate-800/50 border-slate-700/50">
                <Brain className="h-4 w-4 text-slate-400" />
                <AlertTitle className="text-slate-300">Load Trend Analysis</AlertTitle>
                <AlertDescription className="text-slate-500">
                  Click "Refresh" to load AI-powered trend analysis across your PIPs.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
